import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, Palette, Type, Component, ShieldCheck, Image as ImageIcon } from "lucide-react";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/StateBlocks";
import { metaService } from "@/services/metaService";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

import { PALETTE, TYPOGRAPHY, BRAND, ASSETS } from "@/config/brand";
import { FOUNDATION } from "@/constants/testIds";

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="space-y-5">
    <div className="flex items-center gap-3">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-navy text-brand-gold">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div>
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

function ServicesPanel() {
  const [state, setState] = useState({ status: "loading", data: null });

  const load = () => {
    setState({ status: "loading", data: null });
    Promise.all([metaService.getMeta(), metaService.getServicesStatus(), metaService.getHealth()])
      .then(([meta, services, health]) => setState({ status: "ready", data: { meta, services, health } }))
      .catch(() => setState({ status: "error", data: null }));
  };

  useEffect(load, []);

  if (state.status === "loading") return <LoadingState label="Checking backend foundation" />;
  if (state.status === "error")
    return <ErrorState title="Backend unreachable" description="Could not reach the AgentHQ API." onRetry={load} />;

  const { meta, services, health } = state.data;
  const rows = [
    { name: "Database (MongoDB)", value: health.checks.database, ok: health.checks.database === "ok" },
    { name: "Authentication / RBAC", value: meta.features.auth ? "enabled" : "boundary ready", ok: false },
    { name: "Billing (Stripe)", value: services.billing.enabled ? "enabled" : "abstraction ready", ok: false },
    { name: "Private Storage", value: services.storage.enabled ? "enabled" : "abstraction ready", ok: false },
    { name: "Notifications", value: services.notifications.enabled ? "enabled" : "abstraction ready", ok: false },
    { name: `${BRAND.assistant} AI (provider-agnostic)`, value: services.ai.default_provider || "no default provider", ok: false },
  ];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Foundation service</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">State</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.name}>
            <TableCell className="font-medium text-foreground">{r.name}</TableCell>
            <TableCell className="text-muted-foreground">{r.value}</TableCell>
            <TableCell className="text-right">
              {r.ok ? (
                <Badge className="bg-success text-success-foreground hover:bg-success">Live</Badge>
              ) : (
                <Badge variant="outline" className="border-accent/40 text-accent">Ready</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function FoundationShowcase() {
  return (
    <AppShell>
      <div className="space-y-12" data-testid={FOUNDATION.page}>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
          <Badge variant="outline" className="mb-4 border-accent/40 text-accent">
            Internal · Phase 0 QA
          </Badge>
          <PageHeader
            eyebrow={`${BRAND.company} Design Foundation`}
            title="Design System & Foundation"
            description="An internal reference to verify the approved RESIDENNCE brand system — colors, typography, components and states — before Phase 1. This page is not a customer-facing product view and simulates no product functionality."
          />
        </div>

        {/* --- Official brand assets --- */}
        <Section icon={ImageIcon} title="Official Brand Assets" subtitle="The approved RESIDENNCE logo suite — the visual source of truth for the application.">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
              <div className="flex min-h-[140px] items-center justify-center bg-brand-navy p-8">
                <img src={ASSETS.logoNavy} alt="RESIDENNCE primary logo" className="max-h-24 w-auto" data-testid="asset-primary-logo" />
              </div>
              <p className="px-4 py-3 text-sm font-medium text-foreground">Primary logo <span className="text-muted-foreground">· on navy</span></p>
            </div>
            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
              <div className="flex min-h-[140px] items-center justify-center bg-brand-navy p-8">
                <img src={ASSETS.wordmark} alt="RESIDENNCE wordmark" className="max-h-16 w-auto" data-testid="asset-wordmark" />
              </div>
              <p className="px-4 py-3 text-sm font-medium text-foreground">Wordmark <span className="text-muted-foreground">· transparent, used in the app shell</span></p>
            </div>
            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
              <div className="flex min-h-[140px] items-center justify-center gap-8 bg-brand-light-gray p-8">
                <img src={ASSETS.mark} alt="RESIDENNCE brand mark" className="h-20 w-20" data-testid="asset-brand-mark" />
                <img src={ASSETS.mark} alt="RESIDENNCE brand mark on navy" className="h-16 w-16 rounded-full bg-brand-navy" />
              </div>
              <p className="px-4 py-3 text-sm font-medium text-foreground">Icon / brand mark</p>
            </div>
            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
              <div className="flex min-h-[140px] items-center justify-center bg-secondary p-8">
                <img src={ASSETS.favicon} alt="RESIDENNCE favicon" className="h-16 w-16 rounded-xl shadow-card" data-testid="asset-favicon" />
              </div>
              <p className="px-4 py-3 text-sm font-medium text-foreground">Favicon</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-border shadow-card">
            <img src={ASSETS.brandBoard} alt="Approved RESIDENNCE brand board" className="w-full" data-testid="asset-brand-board" />
          </div>
          <p className="text-xs text-muted-foreground">
            These official assets replace the earlier temporary typographic wordmark and are the authoritative source of truth. Playfair Display is used for selective brand moments; Manrope for all application UI.
          </p>
        </Section>

        {/* --- Backend / architecture status --- */}
        <Section icon={ShieldCheck} title="Foundation Services" subtitle="Live status of the backend architecture and future-service boundaries.">
          <Card>
            <CardContent className="p-0">
              <ServicesPanel />
            </CardContent>
          </Card>
        </Section>

        {/* --- Colors --- */}
        <Section icon={Palette} title="Brand Palette" subtitle="Exact approved brand colors. Light-first application surfaces.">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {PALETTE.map((c) => (
              <div
                key={c.token}
                data-testid={FOUNDATION.colorSwatch(c.token)}
                className="overflow-hidden rounded-xl border border-border bg-card shadow-card"
              >
                <div className="h-20 w-full" style={{ backgroundColor: c.hex }} />
                <div className="space-y-1 p-3">
                  <p className="text-sm font-semibold text-foreground">{c.name}</p>
                  <p className="font-mono text-xs uppercase text-muted-foreground">{c.hex}</p>
                  <p className="text-[11px] leading-snug text-muted-foreground">{c.role}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* --- Typography --- */}
        <Section icon={Type} title="Typography" subtitle={`${TYPOGRAPHY.brand.name} for brand moments · ${TYPOGRAPHY.ui.name} for the UI.`}>
          <Card>
            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-accent">Brand serif — Playfair Display</p>
                <p className="font-brand text-5xl font-semibold text-foreground">Residennce</p>
                <p className="font-brand text-2xl text-muted-foreground">Elegant editorial headings, used selectively.</p>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-accent">UI sans — Manrope</p>
                <p className="text-3xl font-bold text-foreground">Heading · text-3xl</p>
                <p className="text-xl font-semibold text-foreground">Subheading · text-xl</p>
                <p className="text-base text-foreground">Body text · the quick brown fox jumps over the lazy dog.</p>
                <p className="text-sm text-muted-foreground">Small / muted · supporting metadata and captions.</p>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* --- Buttons & Badges --- */}
        <Section icon={Component} title="Buttons, Badges & Inputs" subtitle="Core interactive elements with accessible focus states.">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Buttons</CardTitle>
                <CardDescription>Primary navy, gold accent, and supporting variants.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <Button data-testid={FOUNDATION.buttonSample("primary")}>Primary</Button>
                  <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid={FOUNDATION.buttonSample("accent")}>
                    Gold Accent
                  </Button>
                  <Button variant="secondary" data-testid={FOUNDATION.buttonSample("secondary")}>Secondary</Button>
                  <Button variant="outline" data-testid={FOUNDATION.buttonSample("outline")}>Outline</Button>
                  <Button variant="ghost" data-testid={FOUNDATION.buttonSample("ghost")}>Ghost</Button>
                  <Button variant="destructive" data-testid={FOUNDATION.buttonSample("destructive")}>Destructive</Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button>Default</Button>
                  <Button size="lg">Large</Button>
                  <Button disabled>Disabled</Button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Badge>Default</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="outline" className="border-accent/40 text-accent">Gold</Badge>
                  <Badge className="bg-success text-success-foreground hover:bg-success">Success</Badge>
                  <Badge variant="destructive">Destructive</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Fields</CardTitle>
                <CardDescription>Accessible labelled inputs.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="demo-name">Full name</Label>
                  <Input id="demo-name" placeholder="Jordan Avery" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="demo-role">Role</Label>
                  <Select>
                    <SelectTrigger id="demo-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="demo-notes">Notes</Label>
                  <Textarea id="demo-notes" placeholder="Add a note…" rows={2} />
                </div>
                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <Checkbox id="demo-check" /> Remember me
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <Switch id="demo-switch" /> Notifications
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* --- Overlays --- */}
        <Section icon={Component} title="Modal & Drawer" subtitle="Overlay surfaces with restrained styling.">
          <div className="flex flex-wrap gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="open-dialog">Open Modal</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-brand">Confirm action</DialogTitle>
                  <DialogDescription>
                    This is a foundation modal style preview. No action is performed.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline">Cancel</Button>
                  <Button>Confirm</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" data-testid="open-drawer">Open Drawer</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle className="font-brand">Panel</SheetTitle>
                  <SheetDescription>Side drawer style used for future detail panels.</SheetDescription>
                </SheetHeader>
              </SheetContent>
            </Sheet>
          </div>
        </Section>

        {/* --- Feedback states --- */}
        <Section icon={Info} title="Feedback States" subtitle="Loading, success, warning and error patterns.">
          <div className="grid gap-4 lg:grid-cols-2">
            <Alert className="border-success/30 bg-success/5" data-testid={FOUNDATION.stateBlock("success")}>
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>The operation completed successfully.</AlertDescription>
            </Alert>
            <Alert className="border-warning/30 bg-warning/5" data-testid={FOUNDATION.stateBlock("warning")}>
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>Something needs your attention.</AlertDescription>
            </Alert>
            <Alert className="border-info/30 bg-info/5" data-testid={FOUNDATION.stateBlock("info")}>
              <Info className="h-4 w-4 text-info" />
              <AlertTitle>Information</AlertTitle>
              <AlertDescription>A neutral informational message.</AlertDescription>
            </Alert>
            <Alert variant="destructive" data-testid={FOUNDATION.stateBlock("error")}>
              <XCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>An error occurred while processing.</AlertDescription>
            </Alert>
          </div>
          <div className="grid gap-4 pt-2 lg:grid-cols-2">
            <Card><CardContent className="p-6"><LoadingState label="Loading data" /></CardContent></Card>
            <Card><CardContent className="p-6"><EmptyState description="No records to display yet." /></CardContent></Card>
          </div>
        </Section>
      </div>
    </AppShell>
  );
}
